import type { Article, ArticlePayload, NodeContent } from '@/modules/table-of-contents';

export function collectArticleIds(articles: ArticlePayload[]): Set<number> {
	const ids = new Set<number>();
	function collect(items: ArticlePayload[]): void {
		for (const item of items) {
			ids.add(item.id);
			if (item.children && item.children.length > 0) {
				collect(item.children);
			}
		}
	}
	collect(articles);
	return ids;
}

export function nodesConvertToArticles(nodes: NodeContent[], currentLanguage: string): Article[] {
	const articles: Article[] = [];
	nodes.forEach((node) => {
		if (node.content.lang[currentLanguage]?.content && node.content.lang[currentLanguage]?.title) {
			articles.push({
				id: node.id,
				title: node.content.lang[currentLanguage].title,
				children: [],
			});
		}
	});
	return articles;
}

export function reduceArticles(articles: Article[], orderedArticles: Article[]): Article[] {
	const getIds = (arr: Article[]): Set<number> => {
		const ids = new Set<number>();
		arr.forEach((item) => {
			ids.add(item.id);
			if (item.children.length > 0) {
				const childIds = getIds(item.children);
				childIds.forEach((id) => ids.add(id));
			}
		});
		return ids;
	};

	const orderedIds = getIds(orderedArticles);
	return articles.filter((article) => !orderedIds.has(article.id));
}

export function deleteObjectById(array: Article[], id: number): void {
	for (let i = 0; i < array.length; i++) {
		if (array[i].id === id) {
			array.splice(i, 1);
			return;
		} else if (array[i].children.length > 0) {
			deleteObjectById(array[i].children, id);
		}
	}
}

export function provideTitleProperty(
	data: ArticlePayload[],
	nodes: NodeContent[],
	currentLanguage: string,
): Article[] {
	const clonedData: Article[] = JSON.parse(JSON.stringify(data));

	function recursiveProvideTitle(obj: Article | Article[]): void {
		if (Array.isArray(obj)) {
			obj.forEach(recursiveProvideTitle);
		} else if (typeof obj === 'object' && obj !== null) {
			const node = nodes.find((n) => n.id === obj.id);

			if (node) {
				obj.title = node.content.lang[currentLanguage].title;
			} else {
				obj.title = 'deleted';
				obj.deleted = true;
			}

			if (obj.children) {
				recursiveProvideTitle(obj.children);
			}
		}
	}

	recursiveProvideTitle(clonedData);
	return clonedData;
}

export function removeTitleProperty(data: Article[]): ArticlePayload[] {
	const clonedData = JSON.parse(JSON.stringify(data));

	function recursiveRemoveTitle(obj: Record<string, unknown> | Record<string, unknown>[]): void {
		if (Array.isArray(obj)) {
			obj.forEach(recursiveRemoveTitle);
		} else if (typeof obj === 'object' && obj !== null) {
			delete obj.title;
			delete obj.deleted;
			if (obj.children) {
				recursiveRemoveTitle(obj.children as Record<string, unknown>[]);
			}
		}
	}

	recursiveRemoveTitle(clonedData);
	return clonedData as ArticlePayload[];
}

export function flattenExceededDepth(array: Article[], maxDepth: number, depth: number = 1): Article[] {
	return array.reduce<Article[]>((acc, item) => {
		if (depth === maxDepth && item.children) {
			const children = item.children;
			item.children = [];
			return acc.concat(item, ...flattenExceededDepth(children, maxDepth, depth));
		} else {
			if (item.children) {
				item.children = flattenExceededDepth(item.children, maxDepth, depth + 1);
			}
			return acc.concat(item);
		}
	}, []);
}

export function flattenStructure(data: Article[]): Article[] {
	return data.reduce<Article[]>((acc, item) => {
		const children = item.children;
		const itemWithoutChildren: Article = { ...item, children: [] };
		if (children && children.length) {
			return acc.concat([itemWithoutChildren], flattenStructure(children));
		} else {
			return acc.concat(itemWithoutChildren);
		}
	}, []);
}
